// E8PieChartTaskCountClosedTask.js
// Zadanie zamkniete E8 (2026 zad. 1) – odczyt z diagramu kolowego + procent liczby.
// Diagram pokazuje procentowy podzial zadan na 5 dzialow; jeden dzial nie ma
// podpisanego procentu (trzeba go policzyc jako dopelnienie do 100%), a nastepnie
// obliczyc liczbe zadan z tego dzialu.

import React from "react";
import { BlockMath, ChoiceOptionMath, InlineMath } from "../../../../components/math/KatexWrappers";
import ABCDInput from "../../../../components/inputs/ABCDInput";

const LETTERS = ["A", "B", "C", "D"];

// 5 dzialow (mianownik + dopelniacz do tresci "liczba zadan z ...")
const DZIALY = [
  { nom: "algebra", gen: "algebry" },
  { nom: "planimetria", gen: "planimetrii" },
  { nom: "stereometria", gen: "stereometrii" },
  { nom: "arytmetyka", gen: "arytmetyki" },
  { nom: "statystyka", gen: "statystyki" },
];

const SLICE_FILLS = [
  "rgba(240,173,61,0.55)",
  "rgba(124,170,200,0.50)",
  "rgba(150,196,120,0.50)",
  "rgba(176,127,208,0.50)",
  "rgba(150,156,168,0.50)",
];

// zestawy procentow (5 dzialow, suma 100, wielokrotnosci 5)
const PCT_BLUEPRINTS = [
  [25, 15, 5, 35, 20],
  [20, 10, 15, 30, 25],
  [30, 20, 10, 25, 15],
  [25, 20, 15, 15, 25],
  [20, 30, 10, 20, 20],
  [35, 10, 20, 20, 15],
];

// totaly dajace calkowite liczby zadan dla procentow bedacych wielokrotnoscia 5
const TOTALS = [20, 40, 100, 200];

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function shuffle(arr) {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

function buildOptions(answer, pool, fixed = null) {
  if (fixed) {
    return {
      options: fixed.map((value, index) => ({ value, label: LETTERS[index] })),
      correctIndex: fixed.indexOf(answer),
    };
  }
  const distractors = [];
  shuffle(pool).forEach((value) => {
    if (value !== answer && value > 0 && !distractors.includes(value) && distractors.length < 3) {
      distractors.push(value);
    }
  });
  let extra = 1;
  while (distractors.length < 3) {
    const cand = answer + extra * 2;
    if (!distractors.includes(cand) && cand !== answer) distractors.push(cand);
    extra += 1;
  }
  const ordered = shuffle([answer, ...distractors]);
  return {
    options: ordered.map((value, index) => ({ value, label: LETTERS[index] })),
    correctIndex: ordered.indexOf(answer),
  };
}

function buildData({ total, percents, askedIndex, fixedOptions = null }) {
  const counts = percents.map((p) => (p * total) / 100);
  const answer = counts[askedIndex];
  const askedPct = percents[askedIndex];
  const pool = [askedPct, total - answer, ...counts.filter((_, i) => i !== askedIndex)];
  const built = buildOptions(answer, pool, fixedOptions);

  return {
    total,
    percents,
    counts,
    askedIndex,
    askedPct,
    answer,
    options: built.options,
    correctIndex: built.correctIndex,
  };
}

function createOriginalTask() {
  // arytmetyka (indeks 3) bez podpisanego procentu = 100-(25+15+5+20)=35% z 40 = 14
  return {
    type: "E8PieChartTaskCountClosedTask",
    data: buildData({
      total: 40,
      percents: [25, 15, 5, 35, 20],
      askedIndex: 3,
      fixedOptions: [14, 18, 26, 35],
    }),
    source: {
      variant: "original",
      exam: "E8",
      session: "2026_MAJ",
      taskNumber: 1,
    },
    tags: ["e8", "statystyka", "diagram-kolowy", "procenty"],
  };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();

  const percents = pick(PCT_BLUEPRINTS);
  const total = pick(TOTALS);
  const askedIndex = Math.floor(Math.random() * DZIALY.length);

  return {
    type: "E8PieChartTaskCountClosedTask",
    data: buildData({ total, percents, askedIndex }),
    source: { variant: "random", basedOn: "E8_2026_MAJ_1" },
    tags: ["e8", "statystyka", "diagram-kolowy", "procenty"],
  };
}

function polar(cx, cy, r, angleDeg) {
  const a = (angleDeg * Math.PI) / 180;
  return { x: cx + r * Math.cos(a), y: cy + r * Math.sin(a) };
}

function PieDiagram({ data }) {
  const line = "currentColor";
  const cx = 150;
  const cy = 125;
  const r = 82;

  let startAngle = -90; // start u gory
  const slices = data.percents.map((pct, index) => {
    const sweep = (pct / 100) * 360;
    const endAngle = startAngle + sweep;
    const p1 = polar(cx, cy, r, startAngle);
    const p2 = polar(cx, cy, r, endAngle);
    const midAngle = startAngle + sweep / 2;
    const labelPt = polar(cx, cy, r + 26, midAngle);
    const largeArc = sweep > 180 ? 1 : 0;
    const d = `M ${cx} ${cy} L ${p1.x.toFixed(1)} ${p1.y.toFixed(1)} A ${r} ${r} 0 ${largeArc} 1 ${p2.x.toFixed(1)} ${p2.y.toFixed(1)} Z`;
    startAngle = endAngle;
    return { index, pct, d, labelPt };
  });

  return (
    <div style={{ display: "flex", justifyContent: "center", margin: "16px 0" }}>
      <svg width="360" height="250" viewBox="0 0 360 250" role="img" aria-label="Diagram kolowy procentowego podzialu zadan">
        {slices.map((s) => (
          <path key={s.index} d={s.d} fill={SLICE_FILLS[s.index]} stroke={line} strokeWidth="1.6" />
        ))}
        {slices.map((s) => (
          <text
            key={`l-${s.index}`}
            x={s.labelPt.x}
            y={s.labelPt.y}
            textAnchor="middle"
            fill="currentColor"
            fontSize="13"
          >
            <tspan x={s.labelPt.x} dy={s.index !== data.askedIndex ? "-0.15em" : "0.35em"}>
              {DZIALY[s.index].nom}
            </tspan>
            {s.index !== data.askedIndex && (
              <tspan x={s.labelPt.x} dy="1.2em">{s.pct}%</tspan>
            )}
          </text>
        ))}
      </svg>
    </div>
  );
}

export function renderTask({ data }) {
  const dzial = DZIALY[data.askedIndex];
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        Test z matematyki składa się z <InlineMath math={`${data.total}`} /> zadań.
        Na diagramie przedstawiono procentowy podział liczby zadań w teście na
        zadania z pięciu działów: algebry, planimetrii, stereometrii, arytmetyki,
        statystyki.
      </p>
      <PieDiagram data={data} />
      <p>Dokończ zdanie. Wybierz właściwą odpowiedź spośród podanych.</p>
      <p>
        Liczba zadań z {dzial.gen} w tym teście jest równa
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: 8, marginTop: 10 }}>
        {data.options.map((option, index) => (
          <div key={option.value}>
            <ChoiceOptionMath letter={LETTERS[index]} math={`${option.value}`} />
          </div>
        ))}
      </div>
    </div>
  );
}

export function renderInput({ data }, userAnswer, onChange, disabled) {
  return (
    <ABCDInput
      options={data.options.map((option, index) => ({
        label: LETTERS[index],
        textNode: LETTERS[index],
      }))}
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  return userAnswer === data.correctIndex;
}

export function renderAnswer({ data }) {
  return (
    <span>
      <ChoiceOptionMath letter={LETTERS[data.correctIndex]} math={`${data.answer}`} />
    </span>
  );
}

export function renderUserAnswer(userAnswer, { data }) {
  if (typeof userAnswer !== "number") return <i>(brak odpowiedzi)</i>;
  const option = data.options[userAnswer];
  if (!option) return <i>(nieprawidłowy wybór)</i>;
  return (
    <span>
      <ChoiceOptionMath letter={LETTERS[userAnswer]} math={`${option.value}`} />
    </span>
  );
}

export function renderExplanation({ data }) {
  const dzial = DZIALY[data.askedIndex];
  const knownPctSum = data.percents
    .filter((_, i) => i !== data.askedIndex)
    .reduce((a, b) => a + b, 0);

  return (
    <div style={{ textAlign: "left", lineHeight: 1.75 }}>
      <p>
        <strong>Krok 1.</strong> Procent zadań z {dzial.gen} to dopełnienie do{" "}
        <InlineMath math="100\%" />, bo na diagramie podano pozostałe działy.
      </p>
      <BlockMath math={`100\\%-${knownPctSum}\\%=${data.askedPct}\\%`} />
      <p>
        <strong>Krok 2.</strong> Obliczamy liczbę zadań z {dzial.gen}, czyli{" "}
        <InlineMath math={`${data.askedPct}\\%`} /> z <InlineMath math={`${data.total}`} />.
      </p>
      <BlockMath
        math={`${data.askedPct}\\%\\cdot${data.total}=\\frac{${data.askedPct}}{100}\\cdot${data.total}=${data.answer}`}
      />
      <p>
        Poprawna odpowiedź to{" "}
        <InlineMath math={`\\text{${LETTERS[data.correctIndex]}}`} />.
      </p>
    </div>
  );
}
